import {
  DeploymentCustomer,
  DeploymentServer,
  DeploymentTargetRecord,
  ManagedArtifactFileStatus,
  ServerLock,
  SetupRecord,
  SetupStackSummary,
  SetupStatus,
  SetupTargetSummary,
  SetupTlsStatus,
} from '../../types/deployment';
import { resolvePreferredSetupMode } from '../../utils/deploymentMode';

export const SETUP_STATUS_LABELS: Record<SetupStatus, string> = {
  requested: 'Requested',
  running: 'Running',
  success: 'Success',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export const SETUP_STATUS_COLORS: Record<SetupStatus, 'success' | 'error' | 'warning' | 'default' | 'info'> = {
  requested: 'default',
  running: 'info',
  success: 'success',
  failed: 'error',
  cancelled: 'default',
};

export const TARGET_SETUP_STATUS_CONFIG: Record<string, { label: string; color: 'success' | 'error' | 'warning' | 'default' | 'info' }> = {
  'not-started': { label: 'Not Set Up', color: 'default' },
  'provisioning': { label: 'Provisioning', color: 'info' },
  'ready': { label: 'Ready', color: 'success' },
  'ready-for-update': { label: 'Ready for Update', color: 'success' },
  'failed-preflight': { label: 'Preflight Failed', color: 'error' },
  'partially-ready': { label: 'Partially Ready', color: 'warning' },
  'failed': { label: 'Setup Failed', color: 'error' },
};

export const getEntityId = (entity: { _id: string } | string | null | undefined): string => {
  if (!entity) {
    return '';
  }

  return typeof entity === 'string' ? entity : entity._id;
};

export const getEntityLabel = (
  entity: { name?: string; slug?: string; hostname?: string; pathWithNamespace?: string } | string | null | undefined
): string => {
  if (!entity) {
    return '—';
  }

  return typeof entity === 'string'
    ? entity
    : entity.pathWithNamespace || entity.name || entity.slug || entity.hostname || '—';
};

export const getCustomerName = (customer: string | DeploymentCustomer | null | undefined) => (
  getEntityLabel(customer)
);

export const getServerName = (server: string | DeploymentServer | null | undefined) => (
  getEntityLabel(server)
);

export const formatDateTime = (dateStr?: string | null): string => {
  if (!dateStr) {
    return '—';
  }

  const date = new Date(dateStr);
  return Number.isNaN(date.getTime()) ? String(dateStr) : date.toLocaleString();
};

export const formatRelativeTime = (dateStr?: string | null): string => {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

export const sortByRecentActivity = (left?: string | null, right?: string | null) => {
  const leftValue = new Date(left || 0).getTime();
  const rightValue = new Date(right || 0).getTime();
  return rightValue - leftValue;
};

export const getSetupActivityAt = (setup: SetupRecord | null | undefined): string => (
  setup?.lastStatusAt
  || setup?.lastProgressAt
  || setup?.updatedAt
  || setup?.createdAt
  || ''
);

export const getLockTargetIds = (lock: ServerLock): string[] => (
  Array.isArray(lock.targets)
    ? lock.targets.map((target) => getEntityId(target)).filter(Boolean)
    : []
);

export const getLockTargetLabels = (lock: ServerLock): string[] => (
  Array.isArray(lock.targets)
    ? lock.targets
      .map((target) => getEntityLabel(target))
      .filter((label) => Boolean(label) && label !== '—')
    : []
);

export const getTargetLocks = (
  target: DeploymentTargetRecord,
  locks: ServerLock[]
): ServerLock[] => {
  const targetId = target._id;
  const serverId = getEntityId(target.server);

  return locks.filter((lock) => {
    const lockTargetIds = getLockTargetIds(lock);

    if (lockTargetIds.length > 0) {
      return lockTargetIds.includes(targetId);
    }

    return getEntityId(lock.server) === serverId;
  });
};

const getSetupMetadata = (
  setup: SetupRecord | null | undefined
): { failureClass?: string; tlsStatus?: SetupTlsStatus | null } => (
  (setup?.metadata as { failureClass?: string; tlsStatus?: SetupTlsStatus | null } | undefined) ?? {}
);

export const getFailureClass = (setup: SetupRecord | null | undefined): string => String(
  setup?.setupProgress?.failureClass
  || getSetupMetadata(setup).failureClass
  || ''
).trim();

export const getFailureMessage = (setup: SetupRecord | null | undefined): string => String(
  setup?.failedStepMessage
  || setup?.errorMessage
  || ''
).trim();

export const getSetupTlsStatus = (setup: SetupRecord | null | undefined): SetupTlsStatus | null => (
  getSetupMetadata(setup).tlsStatus ?? null
);

const FAILURE_CLASS_SUMMARY: Record<string, string> = {
  'lock-conflict': 'Another setup or deploy already holds the server lock.',
  'host-unreachable': 'The server could not be reached over SSH.',
  'missing-sudo': 'The deploy user does not have non-interactive sudo.',
  'low-disk': 'The target host does not have enough free disk.',
  'low-memory': 'The target host does not have enough free memory.',
  'port-conflict': 'A required host port is already in use.',
  'host-namespace-conflict': 'A host path, compose identifier, or Nginx namespace conflicts with another app.',
  'checkpoint-manifest-mismatch': 'The stored checkpoint no longer matches the current setup contract.',
  'checkpoint-resume-blocked': 'A previous interrupted setup run must be reconciled before retrying.',
  'healthcheck-failed': 'The service did not become healthy before setup timed out.',
  'missing-healthcheck': 'A required health-check URL is missing for the next stack phase.',
  'dependency-healthcheck-failed': 'A dependency target did not become healthy in time.',
  'rate-limited': 'A control-plane call was rate-limited and needs a retry.',
  'quota-exceeded': 'A control-plane quota or resource limit was exceeded.',
  'control-plane-unavailable': 'A control-plane dependency was temporarily unavailable.',
};

export const formatFailureClassSummary = (failureClass: string): string => {
  if (!failureClass) {
    return '';
  }

  return FAILURE_CLASS_SUMMARY[failureClass]
    || failureClass.split('-').join(' ');
};

export const getTargetFailureClass = (
  target: DeploymentTargetRecord | null | undefined,
  latestSetup: SetupRecord | null
): string => (
  getFailureClass(latestSetup)
  || String(target?.setupState?.lastFailureClass || '').trim()
);

export const getTargetFailureMessage = (
  target: DeploymentTargetRecord | null | undefined,
  latestSetup: SetupRecord | null
): string => (
  getFailureMessage(latestSetup)
  || String(target?.setupState?.lastFailureMessage || '').trim()
);

export const getTargetTlsStatus = (
  target: DeploymentTargetRecord | null | undefined,
  latestSetup: SetupRecord | null
): SetupTlsStatus | null => (
  getSetupTlsStatus(latestSetup)
  || target?.setupState?.tlsStatus
  || null
);

export const shouldShowTlsStatus = (tlsStatus: SetupTlsStatus | null | undefined): boolean => (
  Boolean(tlsStatus)
  && tlsStatus?.managed !== false
  && tlsStatus?.state !== 'not-applicable'
);

export const getTlsChipColor = (
  tlsStatus: SetupTlsStatus | null | undefined
): 'success' | 'warning' | 'info' | 'default' => {
  if (!tlsStatus) {
    return 'default';
  }

  if (tlsStatus.state === 'ready') {
    return 'success';
  }

  if (tlsStatus.state === 'warning') {
    return 'warning';
  }

  return 'info';
};

export const getTlsChipLabel = (tlsStatus: SetupTlsStatus | null | undefined): string => {
  if (!tlsStatus) {
    return 'TLS status';
  }

  if (tlsStatus.state === 'ready') {
    return 'TLS healthy';
  }

  if (tlsStatus.state === 'warning') {
    return 'TLS attention';
  }

  return tlsStatus.state ? 'TLS status' : 'TLS not checked';
};

export const formatTlsSummary = (tlsStatus: SetupTlsStatus | null | undefined): string => {
  if (!tlsStatus) {
    return '';
  }

  if (tlsStatus.summary) {
    return tlsStatus.summary;
  }

  const parts: string[] = [];
  if (tlsStatus.domain) {
    parts.push(`TLS ready for ${tlsStatus.domain}.`);
  }
  if (tlsStatus.certificateExpiresAt) {
    parts.push(`Certificate expires ${formatDateTime(tlsStatus.certificateExpiresAt)}.`);
  }
  if (tlsStatus.renewalSource) {
    parts.push(`Renewal source: ${tlsStatus.renewalSource}.`);
  }

  return parts.join(' ').trim();
};

export const formatTlsTooltip = (tlsStatus: SetupTlsStatus | null | undefined): string => {
  if (!tlsStatus) {
    return '';
  }

  const parts = [formatTlsSummary(tlsStatus)];
  if (tlsStatus.certificateExpiresAt) {
    parts.push(`Expires ${formatDateTime(tlsStatus.certificateExpiresAt)}.`);
  }
  if (tlsStatus.renewalSource) {
    parts.push(
      `Renewal ${tlsStatus.renewalSource} enabled=${tlsStatus.renewalEnabled ? 'yes' : 'no'} active=${tlsStatus.renewalActive ? 'yes' : 'no'}.`
    );
  }
  if (tlsStatus.renewalTimerStatus) {
    parts.push(`Timer: ${tlsStatus.renewalTimerStatus}.`);
  }
  if (tlsStatus.activeCertificateFingerprint) {
    parts.push(`Active fingerprint: ${tlsStatus.activeCertificateFingerprint}.`);
  }
  if (tlsStatus.nginxObservedFingerprint) {
    parts.push(`Nginx fingerprint: ${tlsStatus.nginxObservedFingerprint}.`);
  }
  if (tlsStatus.brokerObservedFingerprint) {
    parts.push(`Broker fingerprint: ${tlsStatus.brokerObservedFingerprint}.`);
  }
  if (tlsStatus.dashboardObservedFingerprint) {
    parts.push(`Dashboard fingerprint: ${tlsStatus.dashboardObservedFingerprint}.`);
  }
  if (tlsStatus.lastRenewalAt) {
    parts.push(`Last renewal: ${formatDateTime(tlsStatus.lastRenewalAt)}.`);
  }
  if (tlsStatus.lastDryRunResult) {
    parts.push(`Last renewal dry-run: ${tlsStatus.lastDryRunResult}${tlsStatus.lastDryRunAt ? ` at ${formatDateTime(tlsStatus.lastDryRunAt)}` : ''}.`);
  }

  return parts.filter(Boolean).join(' ');
};

export const formatManagedArtifactRequiredScope = (
  scope: ManagedArtifactFileStatus['requiredScope']
): string => (
  scope
    ? [
      `customer ${scope.customerId}`,
      `repository ${scope.repositoryId}`,
      `server ${scope.serverId}`,
      `environment ${scope.environment}`,
    ].join(' · ')
    : ''
);

type CredentialCopyEvent = NonNullable<SetupRecord['credentialCopyEvents']>[number];

const getLatestCredentialEvent = (
  setup: SetupRecord | null | undefined
): CredentialCopyEvent | null => {
  const events = setup?.credentialCopyEvents ?? [];
  return events.length > 0 ? events[events.length - 1] : null;
};

export const canTargetReprovisionCredentials = (target: DeploymentTargetRecord | null | undefined): boolean => {
  const server = target?.server && typeof target.server === 'object' ? target.server : null;
  const allowedTargetIds = server?.bootstrap?.allowedCredentialCopyTargetIds ?? [];
  return !!target && allowedTargetIds.includes(target._id);
};

export const getTargetCredentialStatus = (
  target: DeploymentTargetRecord,
  latestSetup: SetupRecord | null,
): { label: string; color: 'success' | 'info' | 'warning'; detail: string } | null => {
  const latestEvent = getLatestCredentialEvent(latestSetup);
  if (latestEvent?.status === 'confirmed') {
    return {
      label: 'Credentials verified',
      color: 'success',
      detail: latestEvent.message
        || `Latest setup confirmed credentials on host at ${latestEvent.destinationPath}.`,
    };
  }

  if (latestEvent?.status === 'staged') {
    return {
      label: 'Credentials staged',
      color: 'info',
      detail: latestEvent.message
        || `Dashboard staged credentials at ${latestEvent.destinationPath}.`,
    };
  }

  const credentialsHostPath = String(target.gcpSetupProfile?.credentialsHostPath || '').trim();
  if (!credentialsHostPath) {
    return null;
  }

  if (canTargetReprovisionCredentials(target)) {
    return {
      label: 'Re-provision available',
      color: 'info',
      detail: `Dashboard can stage credentials to ${credentialsHostPath} before rerunning setup.`,
    };
  }

  return {
    label: 'Manual credential staging',
    color: 'warning',
    detail: `Stage credentials at ${credentialsHostPath} before rerunning setup because re-provision is not enabled for this target.`,
  };
};

export const getSetupDryRunReport = (setup: SetupRecord | null | undefined) => setup?.metadata?.dryRun ?? null;

export const formatDryRunSummary = (
  summary: {
    passCount?: number;
    warnCount?: number;
    failCount?: number;
    skipCount?: number;
    totalCount?: number;
    label?: string;
  } | null | undefined
): string => {
  if (!summary) {
    return '';
  }

  if (summary.label) {
    return summary.label;
  }

  return `${summary.passCount || 0} passed, ${summary.warnCount || 0} warnings, ${summary.failCount || 0} failed, ${summary.skipCount || 0} skipped`;
};

export const getProgressSummaryLines = (setup: SetupRecord): string[] => {
  const lines: string[] = [];
  const steps = setup.steps ?? [];

  if (steps.length > 0) {
    const completedCount = steps.filter((step) => ['success', 'skipped'].includes(step.status)).length;
    lines.push(`Progress: ${completedCount}/${steps.length} steps complete`);

    const activeStep = steps.find((step) => step.status === 'running')
      || steps.find((step) => step.name === setup.currentStep || step.name === setup.failedStep)
      || steps[steps.length - 1];

    if (activeStep?.message && activeStep.message !== setup.failedStepMessage) {
      lines.push(activeStep.message);
    }
  }

  const progressMeta = (setup.metadata as { progressRawFallback?: boolean } | undefined) ?? {};
  if (progressMeta.progressRawFallback) {
    lines.push('Progress fallback: using coarse pipeline/job status');
  }

  const failureClass = getFailureClass(setup);
  if (failureClass) {
    const failureSummary = formatFailureClassSummary(failureClass);
    lines.push(
      failureSummary && failureSummary !== failureClass
        ? `Failure: ${failureSummary} (${failureClass})`
        : `Failure: ${failureSummary || failureClass}`
    );
  }

  const tlsStatus = getSetupTlsStatus(setup);
  const tlsSummary = formatTlsSummary(tlsStatus);
  if (tlsSummary && !lines.includes(tlsSummary) && !lines.includes(`TLS: ${tlsSummary}`)) {
    lines.push(`TLS: ${tlsSummary}`);
  }

  const dryRunReport = getSetupDryRunReport(setup);
  if (dryRunReport) {
    const dryRunSummary = formatDryRunSummary(dryRunReport.summary);
    if (dryRunSummary) {
      lines.push(`Dry run: ${dryRunSummary}`);
    }
    (dryRunReport.highlights || []).slice(0, 2).forEach((highlight) => {
      if (highlight && !lines.includes(highlight)) {
        lines.push(highlight);
      }
    });
  }

  return lines;
};

export const formatSetupActionLabel = (actionType: SetupRecord['actionType']): string => {
  if (actionType === 'managed-artifact-reconcile') {
    return 'Reconcile Managed Files';
  }
  if (actionType === 'certificate-check') {
    return 'Check TLS';
  }
  if (actionType === 'tls-issue') {
    return 'Issue TLS';
  }
  if (actionType === 'tls-renew-dry-run') {
    return 'Renewal Dry Run';
  }
  if (actionType === 'tls-renew') {
    return 'Renew If Due';
  }
  if (actionType === 'dry-run') {
    return 'Dry Run';
  }
  if (actionType === 'stack-dry-run') {
    return 'Stack Dry Run';
  }
  if (actionType === 're-provision-credentials') {
    return 'Re-provision Credentials';
  }
  if (actionType === 'failed-preflight') {
    return 'Failed Preflight';
  }
  if (actionType === 'stack-run') {
    return 'Stack Run';
  }
  return actionType === 'check' ? 'Check' : 'Setup';
};

export const getStackRunPhases = (stackRun: SetupRecord) => {
  const phases = stackRun.metadata?.phases;
  return Array.isArray(phases) ? phases : [];
};

export const getTargetSummaryActivityAt = (summary: SetupTargetSummary): string => (
  getSetupActivityAt(summary.latestSetup)
  || summary.target.setupState?.lastSetupAt
  || ''
);

export const getStackSummaryActivityAt = (summary: SetupStackSummary): string => (
  getSetupActivityAt(summary.latestRun)
);

export const matchesSetupSearch = (summary: SetupTargetSummary, search: string): boolean => {
  const normalizedSearch = search.trim().toLowerCase();
  if (!normalizedSearch) {
    return true;
  }

  const fields = [
    getEntityLabel(summary.target),
    getEntityLabel(summary.target.repository),
    getEntityLabel(summary.target.server),
    getEntityLabel(summary.target.customer),
    summary.target.environment,
    summary.target.setupState?.status,
    summary.latestSetup?.actionType,
    summary.latestSetup?.status,
    summary.latestSetup?.currentStep,
    getTargetFailureClass(summary.target, summary.latestSetup),
    getTargetFailureMessage(summary.target, summary.latestSetup),
  ];

  return fields.some((field) => String(field || '').toLowerCase().includes(normalizedSearch));
};

export const matchesStackSearch = (summary: SetupStackSummary, search: string): boolean => {
  const normalizedSearch = search.trim().toLowerCase();
  if (!normalizedSearch) {
    return true;
  }

  const candidateTargets = summary.candidate?.phases?.flatMap((phase) => phase.targets.map((target) => target.targetName)) || [];
  const fields = [
    summary.label,
    summary.environment,
    getEntityLabel(summary.server),
    getEntityLabel(summary.customer),
    summary.latestRun?.status,
    summary.latestRun?.currentStep,
    summary.latestRun?.failedStep,
    ...candidateTargets,
  ];

  return fields.some((field) => String(field || '').toLowerCase().includes(normalizedSearch));
};

export const getTargetStatusFilterValue = (summary: SetupTargetSummary): string => (
  String(summary.latestSetup?.status || summary.target.setupState?.status || '').trim()
);

export const getStackStatusFilterValue = (summary: SetupStackSummary): string => (
  String(summary.latestRun?.status || '').trim()
);

export const getTargetActionFilterValue = (summary: SetupTargetSummary): string => (
  String(summary.latestSetup?.actionType || '').trim()
);

export const getStackActionFilterValue = (summary: SetupStackSummary): string => (
  summary.latestRun?.actionType || 'stack-run'
);

export const getTargetPreferredSetupMode = (target: DeploymentTargetRecord) => resolvePreferredSetupMode(target);

export const getServerGroupMetrics = (group: {
  activeLocks: ServerLock[];
  targetSummaries: SetupTargetSummary[];
  stackSummaries: SetupStackSummary[];
}) => {
  const activeSetups = group.targetSummaries.filter((summary) => {
    const status = summary.latestSetup?.status;
    return status === 'requested' || status === 'running';
  }).length;

  const failedTargets = group.targetSummaries.filter((summary) => {
    const setupStatus = summary.latestSetup?.status;
    const targetStatus = summary.target.setupState?.status;
    return setupStatus === 'failed' || targetStatus === 'failed' || targetStatus === 'failed-preflight';
  }).length;

  const failedStacks = group.stackSummaries.filter((summary) => summary.latestRun?.status === 'failed').length;

  return {
    lockCount: group.activeLocks.length,
    activeSetups,
    failedTargets,
    failedStacks,
    targetCount: group.targetSummaries.length,
    stackCount: group.stackSummaries.length,
  };
};
